import { SideDrawer } from '@care/react-component-lib';
import { Box, Button, Grid, useMediaQuery, Typography, type Theme } from '@mui/material';
import he from 'he';
import React, { useEffect, useState } from 'react';
import sanitizeHtml from 'sanitize-html';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import { handleFilterAnimation, getFilterData } from '@/utils/filters';
import {
  SEOSegmentationSearchFilters as Filter,
  SEOSegmentationSearchingFor,
} from '@/__generated__/globalTypes';
import sortFilters from '@/utils/sortFilters';

import { SxClassProps } from '@/types/common';
import Age from './Age';
import Availability from './Availability';
import Distance from './Distance';
import PayRate from './PayRate';
import Services from './Services';
import Schedule from './Schedule';
import Hours from './Hours';
import YearsOfExp from './YearsOfExp';
import MoreFilters from './MoreFilters';
import ExperienceWithAges from './ExperienceWithAges';
import { clearFilters } from '../../../utils/filtersStorage';
import JobDetails from './JobDetails';
import JobType from './JobType';

const classes: SxClassProps = {
  btnFilter: (theme) => ({
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    width: 'auto',
    height: theme.spacing(4),
    minWidth: theme.spacing(10),
    borderRadius: theme.spacing(1),
    borderColor: theme.palette.care.grey[300],
  }),
  clearAll: (theme) => ({
    marginLeft: theme.spacing(2.5),
    display: 'inline',
    cursor: 'pointer',
    color: '#015AC8',
    [theme.breakpoints.down('md')]: {
      marginLeft: theme.spacing(-1.5),
    },
  }),
};

interface Props {
  searchFilters: Filter[] | null;
  searchResultsHeader: string;
  searchFiltersRef: React.Ref<HTMLDivElement>;
  onChange(): void;
  enrollmentURL: string;
  serviceId: string;
  serviceIdForMember: string;
  showMoreFilters: boolean;
  showJobDetails: boolean;
  searchingFor: string;
}

const Filters = ({
  searchFilters,
  searchResultsHeader,
  onChange,
  searchFiltersRef,
  enrollmentURL,
  serviceIdForMember,
  serviceId,
  showMoreFilters,
  showJobDetails,
  searchingFor,
}: Props) => {
  const [isSideDrawerOpen, setIsSideDrawerOpen] = useState(false);
  const [isClear, setIsClear] = useState(false);
  const [displayFilter, setDisplayFilter] = useState(false);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [hasMoreFilters, setHasMoreFilters] = useState(false);
  const [hasJobDetails, setHasJobDetails] = useState(false);

  // All job segmentation pages have the same filters
  const allJobFilters: Filter[] = [Filter.DISTANCE, Filter.JOB_TYPE, Filter.PAY_RATE];

  const handleApplyChanges = () => {
    const filterData = getFilterData();
    setDisplayFilter(filterData.displayFilter);
    setHasMoreFilters(filterData.moreFilter);
    setHasJobDetails(filterData.jobDetailsFilter);
    if (filterData.displayFilter && !isClear) {
      onChange();
    }
  };

  const trackEvent = (filter: string) => () => {
    const amplitudeData = {
      filter,
    };

    trackAmplitudeEvent(AmplitudeEvent.clickOnSegmentationFilter, amplitudeData);
  };

  const clearFiltersData = () => {
    clearFilters();
    setIsClear(true);
    handleApplyChanges();
  };

  // Call clearFilters function on page load to remove localStorage data from filters
  useEffect(() => {
    clearFilters();
  }, []);

  useEffect(() => {
    if (isClear) {
      setIsClear(false);
      onChange();
    }
  }, [isClear, onChange]);
  const sideDrawerClose = () => setIsSideDrawerOpen(false);
  const handleMobileAnimation = () => handleFilterAnimation(isMobile, searchFiltersRef, '#navMain');
  const flipSideDrawer = () => {
    trackEvent('More Filters')();
    setIsSideDrawerOpen(!isSideDrawerOpen);
  };

  const renderFilterOptions = (name: string) => {
    switch (name) {
      case Filter.AGE:
        return (
          <Age handleClick={trackEvent(name)} isClear={isClear} onChange={handleApplyChanges} />
        );
      case Filter.AVAILABILITY:
        return (
          <Availability
            handleClick={trackEvent(name)}
            isClear={isClear}
            onChange={handleApplyChanges}
          />
        );
      case Filter.DISTANCE:
        return (
          <Distance
            handleClick={trackEvent(name)}
            isClear={isClear}
            onChange={handleApplyChanges}
          />
        );
      case Filter.EXPERIENCE_WITH_AGES:
        return (
          <ExperienceWithAges
            handleClick={trackEvent(name)}
            isClear={isClear}
            onChange={handleApplyChanges}
          />
        );
      case Filter.HOURS:
        return (
          <Hours handleClick={trackEvent(name)} isClear={isClear} onChange={handleApplyChanges} />
        );
      case Filter.JOB_TYPE:
        return (
          <JobType handleClick={trackEvent(name)} isClear={isClear} onChange={handleApplyChanges} />
        );
      case Filter.PAY_RATE:
        return (
          <PayRate
            handleClick={trackEvent(name)}
            isClear={isClear}
            onChange={handleApplyChanges}
            isJobSearch={showJobDetails}
          />
        );
      case Filter.SCHEDULE:
        return (
          <Schedule
            handleClick={trackEvent(name)}
            isClear={isClear}
            onChange={handleApplyChanges}
          />
        );
      case Filter.SERVICES:
      case Filter.SUBJECT_AREA:
        return (
          <Services
            handleClick={trackEvent(name)}
            isClear={isClear}
            onChange={handleApplyChanges}
            serviceId={serviceId}
          />
        );
      case Filter.YEARS_OF_EXPERIENCE:
        return (
          <YearsOfExp
            handleClick={trackEvent(name)}
            isClear={isClear}
            onChange={handleApplyChanges}
          />
        );
      default:
        return null;
    }
  };

  const getFilters = () => {
    if (
      searchingFor === SEOSegmentationSearchingFor.ALL_JOB ||
      searchingFor === SEOSegmentationSearchingFor.SEEKER_JOB
    ) {
      return allJobFilters;
    }

    if (!searchFilters) return [];

    return sortFilters(searchFilters, serviceIdForMember);
  };

  const filtersList = getFilters();

  return (
    <Box pt={3} ref={searchFiltersRef}>
      <Typography
        variant="h2"
        fontWeight="400"
        lineHeight="24px"
        mb={3}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(he.decode(searchResultsHeader)),
        }}
        sx={(theme) => ({
          color: theme.palette.care.grey[900],
        })}
      />
      <Grid container>
        {isMobile && (
          <>
            <Grid item xs={10}>
              <Typography variant="h4" color="primary" mb={1}>
                Filter by:
              </Typography>
            </Grid>
            <Grid item xs={2}>
              {displayFilter && (
                <Typography variant="body2" onClick={clearFiltersData} sx={classes.clearAll}>
                  Clear All
                </Typography>
              )}
            </Grid>
          </>
        )}
        <Box
          sx={{
            overflowX: 'scroll',
            overflowY: 'hidden',
            whiteSpace: 'nowrap',
            '&::-ms-overflow-style': 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}>
          {!isMobile && (
            <Typography variant="h4" color="primary" display="inline-block" mr={2}>
              Filter by:
            </Typography>
          )}
          {filtersList.map((filterOption) => (
            <Grid
              item
              xs={12}
              md={11}
              display="inline-block"
              mr={2}
              key={filterOption}
              onClick={handleMobileAnimation}>
              {renderFilterOptions(filterOption)}
            </Grid>
          ))}
          {showMoreFilters && (
            <>
              <Button
                variant={hasMoreFilters ? 'contained' : 'outlined'}
                sx={classes.btnFilter}
                color={hasMoreFilters ? 'secondary' : 'inherit'}
                onClick={flipSideDrawer}>
                <Typography variant="body3">More Filters</Typography>
              </Button>
              <SideDrawer open={isSideDrawerOpen} onClose={sideDrawerClose}>
                <MoreFilters
                  onClose={sideDrawerClose}
                  isClear={isClear}
                  onChange={handleApplyChanges}
                  enrollmentURL={enrollmentURL}
                />
              </SideDrawer>
            </>
          )}
          {showJobDetails && (
            <>
              <Button
                variant={hasJobDetails ? 'contained' : 'outlined'}
                sx={classes.btnFilter}
                color={hasJobDetails ? 'secondary' : 'inherit'}
                onClick={flipSideDrawer}>
                <Typography variant="body3">Job Details</Typography>
              </Button>
              <SideDrawer open={isSideDrawerOpen} onClose={sideDrawerClose}>
                <JobDetails
                  onClose={sideDrawerClose}
                  isClear={isClear}
                  onChange={handleApplyChanges}
                  enrollmentURL={enrollmentURL}
                />
              </SideDrawer>
            </>
          )}
          {displayFilter && !isMobile && (
            <Typography variant="body2" onClick={clearFiltersData} sx={classes.clearAll}>
              Clear All
            </Typography>
          )}
        </Box>
      </Grid>
    </Box>
  );
};

export default Filters;
